import React, { useEffect, useState } from "react";
import { Search, PhoneCall, ExternalLink, ShieldCheck, FileText, Home as HomeIcon, Scale, Briefcase } from "lucide-react";
import { RESOURCES } from "../data/content";

const CATEGORIES = [
  { id: "all", label: "All Resources" },
  { id: "crisis", label: "Crisis Hotlines" },
  { id: "housing", label: "Housing" },
  { id: "va_benefits", label: "VA Healthcare & Disability" },
  { id: "legal", label: "Legal Aid" },
  { id: "employment", label: "Job Training" },
];

const categoryIcon = (category) => {
  switch (category) {
    case "crisis": return <PhoneCall className="w-5 h-5 text-red-600" />;
    case "housing": return <HomeIcon className="w-5 h-5 text-navy" />;
    case "va_benefits": return <FileText className="w-5 h-5 text-navy" />;
    case "legal": return <Scale className="w-5 h-5 text-navy" />;
    case "employment": return <Briefcase className="w-5 h-5 text-navy" />;
    default: return <ShieldCheck className="w-5 h-5 text-navy" />;
  }
};

const Resources = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => { document.title = "Resources | Iron Shield Veterans Reset Center"; }, []);

  const filtered = RESOURCES.filter((res) => {
    const matchesCategory = selectedCategory === "all" || res.category === selectedCategory;
    const matchesSearch =
      res.title.toLowerCase().includes(query.toLowerCase()) ||
      res.description.toLowerCase().includes(query.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-offwhite pb-20" data-testid="resources-page">
      {/* Header banner */}
      <section className="bg-navy text-offwhite pt-[130px] md:pt-[150px] pb-12 border-b-4 border-gold">
        <div className="container-x">
          <div className="max-w-3xl space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-gold" />
              <span className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Veteran Support Network</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight">VA & Community Resource Directory</h1>
            <p className="text-offwhite/80 text-sm sm:text-base leading-relaxed">
              A curated directory of national veteran crisis lines, HUD-VASH housing specialists, legal aid attorneys, and North Carolina benefit advisors.
            </p>
          </div>
        </div>
      </section>

      <div className="container-x pt-10">
        {/* Search + filter */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-2xl mx-auto">
            <Search className="w-5 h-5 text-slate absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search resource directory (e.g., HUD-VASH, legal, crisis)…"
              data-testid="resources-search"
              className="w-full pl-12 pr-4 py-3.5 rounded-full border border-softgray bg-white text-sm text-charcoal shadow-sm focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`resources-cat-${cat.id}`}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  selectedCategory === cat.id ? "bg-navy text-offwhite" : "bg-white text-slate border border-softgray hover:border-navy"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
          {filtered.map((res) => (
            <div key={res.id} className="bg-white rounded-xl p-6 border border-softgray hover:border-gold transition-colors flex flex-col justify-between brand-card-shadow" data-testid={`resource-${res.id}`}>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-lg bg-offwhite border border-softgray">{categoryIcon(res.category)}</div>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${res.isNational ? "bg-blue-50 text-blue-800" : "bg-emerald-50 text-emerald-800"}`}>
                    {res.isNational ? "National" : "North Carolina"}
                  </span>
                </div>
                <h3 className="font-bold text-navy text-base mb-2">{res.title}</h3>
                <p className="text-xs text-slate leading-relaxed mb-4">{res.description}</p>
              </div>
              <div className="pt-4 border-t border-softgray space-y-2">
                {res.phone && (
                  <a href={`tel:${res.phone.replace(/[^0-9]/g, "")}`} className="w-full bg-navy text-offwhite py-2.5 px-3 rounded-lg text-xs font-bold hover:bg-navy-deep transition-colors flex items-center justify-center gap-2">
                    <PhoneCall className="w-3.5 h-3.5 text-gold" /> Call: {res.phone}
                  </a>
                )}
                {res.website && (
                  <a href={res.website} target="_blank" rel="noopener noreferrer" className="w-full bg-offwhite text-navy border border-softgray py-2.5 px-3 rounded-lg text-xs font-semibold hover:bg-softgray/40 transition-colors flex items-center justify-center gap-1.5">
                    Visit Official Portal <ExternalLink className="w-3.5 h-3.5 text-slate" />
                  </a>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate" data-testid="resources-empty">No resources match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Resources;
